import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Alerts from "../../Components/Utils/Alert";
import {
  useLazyGetDeviceDetailQuery,
  useLazyGetDeviceDistributionQuery,
} from "../../store/storageRoles/devicesSharedApi";
import useAuth from "../../Hooks/useAuth";
import usePersistedFilters from "../../Hooks/usePersistedFilters";
import { PageShell } from "../../Components/shared";
import DeviceOverview from "../../Components/Device/storageRoles/DeviceOverview";
import DeviceDetailPageHeader from "../../Components/Device/DeviceDetailPageHeader";

function DetailDevicePage() {
  const { auth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [alert, setAlert] = useState(0);
  const [state, setState] = useState(null);
  const [message, setMessage] = useState("");
  const [devices, setDevices] = useState([]);
  const [operationHandler, setOperationHandler] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedOperationDevice, setSelectedOperationDevice] = useState(null);
  const [deviceDetailClick, setDeviceDetailClick] = useState(null);
  const savedFilters = location.state?.deviceListFilters;
  // Filtreler sessionStorage'da tutulur: detaya gidip geri dönünce (browser
  // geri tuşu dahil) liste aynı filtre/sayfayla açılır. location.state ile
  // gelen değerler (geri dönüş state'i veya deep-link araması) storage'ı ezer.
  const [searchParams, setSearchParams] = usePersistedFilters(
    "deviceList:storage:filters",
    {
      serialNo: "",
      deviceName: "",
      firmName: "",
      cupNumber: "",
      deviceStatusName: "all",
      page: 1,
      limit: 10,
    },
    savedFilters ||
      (location.state?.searchSerialNo
        ? { serialNo: location.state.searchSerialNo, page: 1 }
        : null),
  );

  const [
    getDeviceDetail,
    {
      data: deviceDetailData,
      isLoading: deviceDetailIsLoading,
      isFetching: deviceDetailIsFetching,
      status: deviceDetailStatus,
      error: deviceDetailError,
    },
  ] = useLazyGetDeviceDetailQuery();

  const [
    getDeviceDistribution,
    {
      data: distributionResponse,
      isLoading: distributionLoading,
      isFetching: distributionFetching,
    },
  ] = useLazyGetDeviceDistributionQuery();

  // İlk liste yüklemesi sadece DevicesList içindeki useEffect ile yapılıyor (tek kaynak, gereksiz çift istek önlendi).

  useEffect(() => {
    if (deviceDetailData?.devices) {
      setDevices(deviceDetailData.devices);

      // Eğer location state'inde selectedDeviceId varsa, direkt route'a yönlendir
      if (location.state?.selectedDeviceId) {
        const targetDevice = deviceDetailData.devices.find(
          (device) => device._id === location.state.selectedDeviceId
        );
        if (targetDevice) {
          navigate(`/Anasayfa/Cihazlar/${targetDevice._id}`, {
            state: { device: targetDevice, fromPage: location.state?.fromPage || "Cihazlar" },
            replace: true,
          });
        }
      }
    }
  }, [deviceDetailData, location.state?.selectedDeviceId, navigate]);

  // Loading state'i takip et - API başarılı olduğunda loading'i kapat
  useEffect(() => {
    if (deviceDetailIsFetching === false && deviceDetailIsLoading === false) {
      // Loading bitti, DevicesList'teki isSearching state'ini de sıfırlamak için
      // Bu parent component'te yapılamaz, DevicesList kendi state'ini yönetiyor
    }
  }, [deviceDetailIsFetching, deviceDetailIsLoading]);

  useEffect(() => {
    if (alert !== 0) {
      setTimeout(() => {
        setAlert(0);
        setMessage("");
      }, 1000);
    }
  }, [alert]);

  const handleDeviceClick = (device) => {
    // Filtreleri state'e koy; geri dönünce aynı filtreyle listeye dönülebilsin
    navigate(`/Anasayfa/Cihazlar/${device._id}`, {
      state: {
        device,
        fromPage: "Cihazlar",
        deviceListFilters: searchParams,
      },
    });
  };
  // Firma adını al
  const firmName = devices[0]?.firm?.firmName || "Firma Bilgisi";
  // Toplam cihaz sayısı: API'den gelen pagination.totalDevices (sayfadaki değil gerçek toplam)
  const totalDevices = deviceDetailData?.pagination?.totalDevices ?? devices.length ?? 0;

  const handleRefresh = async () => {
    if (auth.accessToken) {
      await getDeviceDetail({
        token: auth.accessToken,
        ...searchParams,
      });
    }
  };

  const handleSearchParamsChange = useCallback((newParams) => {
    // Page paramı varsa resetleme, yoksa arama değişiminde 1'e dön
    if (Object.prototype.hasOwnProperty.call(newParams, "page")) {
      setSearchParams((prev) => ({ ...prev, ...newParams }));
    } else {
      setSearchParams((prev) => ({ ...prev, ...newParams, page: 1 }));
    }
  }, [setSearchParams]);

  const handlePageChange = useCallback((page) => {
    setSearchParams((prev) => ({ ...prev, page }));
  }, [setSearchParams]);

  const handleDeviceDetailClick = (deviceOrAction) => {
    // Handle both device objects and action strings ("all", "create", "apk", "hardware")
    if (deviceOrAction === "all" || deviceOrAction === "create" || deviceOrAction === "apk" || deviceOrAction === "hardware" || deviceOrAction === "card") {
      setDeviceDetailClick(deviceOrAction);
      if (deviceOrAction === "all" && auth.accessToken) {
        getDeviceDistribution({ token: auth.accessToken });
      }
    } else if (deviceOrAction && typeof deviceOrAction === "object") {
      setDeviceDetailClick(deviceOrAction);
    } else {
      setDeviceDetailClick(null);
    }
  };

  return (
    <PageShell>
      {alert !== 0 && (
        <div className="fixed inset-0 z-[9999] pointer-events-none flex justify-center pt-4 px-4">
          <div className="pointer-events-auto">
            <Alerts AlertChoice={alert} message={message} />
          </div>
        </div>
      )}

      {/* Sayfa Başlığı */}
      <DeviceDetailPageHeader
          deviceDetailIsLoading={deviceDetailIsLoading || deviceDetailIsFetching}
          firmName={firmName}
          totalDevices={totalDevices}
          devices={devices}
          setDevices={setDevices}
          deviceRevised={handleRefresh}
          onRefresh={handleRefresh}
          onDeviceClick={deviceDetailClick}
          setSelectedDevice={setSelectedDevice}
          operationHandler={operationHandler}
          setOperationHandler={setOperationHandler}
          selectedOperationDevice={selectedOperationDevice}
          setSelectedOperationDevice={setSelectedOperationDevice}
          onDeviceDetailClick={handleDeviceDetailClick}
          state={state}
        />

      {/* Cihazların genel görünümü */}
      <div className="flex-1 min-h-0 flex flex-col">
        <DeviceOverview
            deviceDetailIsLoading={deviceDetailIsLoading || deviceDetailIsFetching}
            deviceDetailIsFetching={deviceDetailIsFetching}
            devicesData={devices}
            totalDevices={totalDevices}
            onDeviceClick={handleDeviceClick}
            deviceRevised={(newDevice) => {
              setDevices((prevDevices) =>
                prevDevices.map((device) =>
                  device._id === newDevice._id ? newDevice : device
                )
              );
            }}
            operationHandler={operationHandler}
            setOperationHandler={setOperationHandler}
            selectedOperationDevice={selectedOperationDevice}
            setSelectedOperationDevice={setSelectedOperationDevice}
            deviceDetailClick={deviceDetailClick}
            setDeviceDetailClick={setDeviceDetailClick}
            searchParams={searchParams}
            onSearchParamsChange={handleSearchParamsChange}
            onPageChange={handlePageChange}
            pagination={deviceDetailData?.pagination}
            getDeviceDetail={getDeviceDetail}
            authToken={auth.accessToken}
            distributionData={distributionResponse?.data}
            distributionLoading={distributionLoading || distributionFetching}
          />
      </div>
    </PageShell>
  );
}

export default DetailDevicePage;
